import {
    MAP_SETTINGS,
    GEOJSON_SETTINGS,
    POPUP_SETTINGS,
    STORAGE_KEYS,
    LOGGING,
    AUTO_OPEN_SINGLE_POPUP,
    UI_VISIBILITY,
    MAP_DATA_STRATEGY,
    SPOT_DISPLAY_MODE
} from '/settings.js';
import { createSpotMarker, clearMarkerRegistry } from '../marker/marker.js';
import { enhanceMapPanning, getOptimizedGeoJsonOptions, panToMarkers } from './map-movements.js';
import { createAnimatedLocationMarker } from './map-controls.js';
import { loadMapData, validateMapData } from './map-data.js';
import { showUserLocation } from '../utils/location-utils.js';
import { getCurrentLanguage } from '../translations/translations.js';
import { initMapLabels } from './map-labels.js';
import { logMarkerCreated, logMarkerRemoved, logSpotsLoaded, takeMemorySnapshot } from '../utils/performance-monitor.js';
import { updateMapView } from './map-transitions.js';
import { parseUrlParams, normalizeSlug } from '../utils/url-utils.js';
import { createAreaMarkers, removeAreaMarkers, updateAreaMarkers } from './area-markers.js';
import { initializeAreaMarkerSelection } from './area-selector-map.js';

// Initialize map
let map;
let geoJsonLayer = null;
export let spots = [];
let markerClusterGroup = null; // Add clustering support
const isCardsMode = SPOT_DISPLAY_MODE === 'CARDS';

let locationMarker = null;
let cachedAreas = null; // Cache areas for area markers
let currentSelectedAreaSlug = null; // Track currently selected area for area markers

export function initializeMap() {
    if (map) {
        map.remove();
    }
    const mapOptions = {
        center: MAP_SETTINGS.DEFAULT_CENTER,
        zoom: MAP_SETTINGS.DEFAULT_ZOOM,
        minZoom: MAP_SETTINGS.MIN_ZOOM,
        maxZoom: MAP_SETTINGS.MAX_ZOOM,
        zoomControl: false,
        attributionControl: false,
        preferCanvas: true,
        updateWhenIdle: false, // keep GeoJSON visible while panning (was true)
        maxBoundsViscosity: 1.0,
        wheelDebounceTime: 40,
        tap: false,
        trackResize: true,
        renderer: L.canvas(),
        rendererFactory: L.canvas,
        panAnimation: true // changed from false to true
    };
    if (MAP_SETTINGS.MAX_BOUNDS_ENABLED) {
        mapOptions.maxBounds = L.latLngBounds(MAP_SETTINGS.MAX_BOUNDS);
        mapOptions.maxBoundsViscosity = MAP_SETTINGS.MAX_BOUNDS_VISCOSITY;
    }
    map = L.map('map', mapOptions);
    enhanceMapPanning(map);

    // Helper function to check if device is mobile
    function isMobile() {
        return window.innerWidth <= 768;
    }

    // Helper function to toggle controls visibility
    function updateControlsVisibility(source = 'popup') {
        let shouldHideControls = false;

        if (SPOT_DISPLAY_MODE === 'POPUPS') {
            // In POPUPS mode, check if a popup is open
            const isPopupOpen = map._popup && map.hasLayer(map._popup);
            shouldHideControls = isPopupOpen ? (isMobile() ? UI_VISIBILITY.HIDE_CONTROLS_ON_POPUPS.MOBILE : UI_VISIBILITY.HIDE_CONTROLS_ON_POPUPS.DESKTOP) : false;
        } else if (SPOT_DISPLAY_MODE === 'CARDS') {
            // In CARDS mode, check if card banner is visible
            const cardsBanner = document.querySelector('.cards-banner-container');
            const isCardBannerOpen = cardsBanner && cardsBanner.classList.contains('show') && cardsBanner.style.display !== 'none';
            shouldHideControls = isCardBannerOpen ? (isMobile() ? UI_VISIBILITY.HIDE_CONTROLS_ON_CARD.MOBILE : UI_VISIBILITY.HIDE_CONTROLS_ON_CARD.DESKTOP) : false;
        }

        if (shouldHideControls) {
            document.body.classList.add('popup-open');
        } else {
            document.body.classList.remove('popup-open');
        }
    }

    // Add event listeners to toggle body class when popup is open/closed (for POPUPS mode)
    map.on('popupopen', () => updateControlsVisibility('popup'));
    map.on('popupclose', () => updateControlsVisibility('popup'));

    // Also listen for card banner visibility changes (for CARDS mode)
    window.addEventListener('cardBannerShow', () => updateControlsVisibility('card'));
    window.addEventListener('cardBannerHide', () => updateControlsVisibility('card'));

    // Initialize marker clustering with fallback
    try {
        if (typeof L.MarkerClusterGroup !== 'undefined') {
            // Option to disable clustering completely (set to true to disable)
            const DISABLE_CLUSTERING = false;

            if (DISABLE_CLUSTERING) {
                // Skip clustering entirely - use individual markers
                if (LOGGING.ENABLED && LOGGING.DEBUG) {
                    console.log('⚠️ Clustering disabled - using individual markers');
                }
            } else {
                markerClusterGroup = L.markerClusterGroup({
                    chunkedLoading: true,
                    chunkInterval: 200,
                    chunkDelay: 50,
                    maxClusterRadius: 0, // No clustering - markers stay individual
                    spiderfyOnMaxZoom: false,
                    showCoverageOnHover: false,
                    zoomToBoundsOnClick: true,
                    disableClusteringAtZoom: 0, // Disable clustering at all zoom levels
                    removeOutsideVisibleBounds: false, // Keep markers visible during panning
                    animate: false, // Disable animations for better performance
                    animateAddingMarkers: false, // Disable for better performance
                    // Hide all cluster icons - show only individual markers
                    iconCreateFunction: function (cluster) {
                        return L.divIcon({
                            html: `<div style="display: none;">${cluster.getChildCount()}</div>`,
                            className: 'marker-cluster-hidden',
                            iconSize: L.point(0, 0)
                        });
                    }
                });
                markerClusterGroup.addTo(map);
                if (LOGGING.ENABLED && LOGGING.DEBUG) {
                    console.log('✅ Marker clustering initialized successfully (individual markers visible)');
                }
            }
        } else {
            if (LOGGING.ENABLED && LOGGING.DEBUG) {
                console.warn('⚠️ Marker clustering library not available, falling back to individual markers');
            }
        }
    } catch (error) {
        if (LOGGING.ENABLED && LOGGING.DEBUG) {
            console.error('❌ Error initializing marker clustering:', error);
            console.warn('⚠️ Falling back to individual markers');
        }
    }

    // Initialize map labels overlay
    initMapLabels(map);
    return map;
}

// Helper to determine map style based on feature and current selection
function getMapStyle(feature) {
    const computedStyle = getComputedStyle(document.documentElement);

    // Get the layer type from the feature properties
    const layerType = feature.properties?._layerType || 'background';

    // Check selection state (UNIFIED strategy logic)
    // If an area is selected in URL, dim other areas
    const { areaSlug } = parseUrlParams();
    const featureName = normalizeSlug(feature.properties?.name || '');
    // If no area selected, or feature has no name, or names match -> it's selected/active
    // Note: La Graciosa might be part of Lanzarote logic or separate? Assuming names match DB slugs.
    const isSelected = !areaSlug || !featureName || (featureName === normalizeSlug(areaSlug));

    // Determine prefix for CSS variables based on selection state and UNIFIED visual options
    let prefix = '--map-'; // Default to normal/selected styling

    if (!isSelected) {
        // For unselected areas, check UNIFIED visual options
        if (MAP_DATA_STRATEGY && MAP_DATA_STRATEGY.TYPE === 'UNIFIED' && MAP_DATA_STRATEGY.UNIFIED_VISUAL_OPTIONS) {
            const visualOptions = MAP_DATA_STRATEGY.UNIFIED_VISUAL_OPTIONS;

            if (layerType === 'roads') {
                // For roads: show normal colors if enabled, otherwise hide
                prefix = visualOptions.SHOW_UNSELECTED_ROADS ? '--map-' : '--map-unselected-';
            } else {
                // For background/borders: grey out if enabled, otherwise use normal colors
                prefix = visualOptions.GREY_OUT_UNSELECTED ? '--map-unselected-' : '--map-';
            }
        } else {
            // Default: use unselected styling if not UNIFIED or no options
            prefix = '--map-unselected-';
        }
    }

    // Apply different styles based on layer type
    switch (layerType) {
        case 'background':
            // Background layer now includes borders merged into it
            // Use background fill for area, and borders CSS variables for stroke/boundary
            const bgFillOpacity = computedStyle.getPropertyValue(`${prefix}background-fill-opacity`).trim();
            const borderStroke = computedStyle.getPropertyValue(`${prefix}borders-stroke`).trim();
            const borderWeight = computedStyle.getPropertyValue(`${prefix}borders-stroke-width`).trim();
            const borderOpacity = computedStyle.getPropertyValue(`${prefix}borders-stroke-opacity`).trim();

            return {
                fillColor: computedStyle.getPropertyValue(`${prefix}background-fill`).trim() || '#D8D6AF',
                fillOpacity: bgFillOpacity ? parseFloat(bgFillOpacity) : 1,
                color: borderStroke || '#c09852',
                weight: borderWeight ? parseFloat(borderWeight) : 1.5,
                opacity: borderOpacity ? parseFloat(borderOpacity) : 0.9
            };
        case 'borders':
            // Kept for backward compatibility if borders layer is separated
            return {
                fillColor: computedStyle.getPropertyValue(`${prefix}borders-fill`).trim() || 'transparent',
                color: computedStyle.getPropertyValue(`${prefix}borders-stroke`).trim() || '#d35400',
                weight: parseFloat(computedStyle.getPropertyValue(`${prefix}borders-stroke-width`).trim()) || 1.5,
                opacity: parseFloat(computedStyle.getPropertyValue(`${prefix}borders-stroke-opacity`).trim()) || 0.8,
                fillOpacity: 0
            };
        case 'roads':
            return {
                fillColor: computedStyle.getPropertyValue(`${prefix}roads-fill`).trim() || 'transparent',
                color: computedStyle.getPropertyValue(`${prefix}roads-stroke`).trim() || '#ffffff',
                weight: parseFloat(computedStyle.getPropertyValue(`${prefix}roads-stroke-width`).trim()) || 0.8,
                opacity: parseFloat(computedStyle.getPropertyValue(`${prefix}roads-stroke-opacity`).trim()) || 0.7,
                fillOpacity: 0
            };
        default:
            // Default to background styling with borders for stroke
            const defaultBgFillOpacity = computedStyle.getPropertyValue(`${prefix}background-fill-opacity`).trim();
            const defaultBorderStroke = computedStyle.getPropertyValue(`${prefix}borders-stroke`).trim();
            const defaultBorderWeight = computedStyle.getPropertyValue(`${prefix}borders-stroke-width`).trim();
            const defaultBorderOpacity = computedStyle.getPropertyValue(`${prefix}borders-stroke-opacity`).trim();

            return {
                fillColor: computedStyle.getPropertyValue(`${prefix}background-fill`).trim() || '#D8D6AF',
                fillOpacity: defaultBgFillOpacity ? parseFloat(defaultBgFillOpacity) : 1,
                color: defaultBorderStroke || '#c09852',
                weight: defaultBorderWeight ? parseFloat(defaultBorderWeight) : 1.5,
                opacity: defaultBorderOpacity ? parseFloat(defaultBorderOpacity) : 0.9
            };
    }
}

// Function to force update map styles (e.g. after URL change)
// Also updates area markers if areaSlug is provided
export function updateMapStyles(areaSlug = null) {
    if (geoJsonLayer) {
        geoJsonLayer.setStyle(getMapStyle);
    }

    // Update area markers if area slug provided and areas are available
    if (areaSlug && cachedAreas && map) {
        currentSelectedAreaSlug = areaSlug;
        updateAreaMarkers(cachedAreas, areaSlug, map);
    }
}

// Load and display map
export async function loadGeoJSONMap(mapInstance, areaSlug = null) {
    if (!mapInstance) {
        console.error('Map object is invalid. Cannot add GeoJSON layer.');
        return;
    }

    try {
        const mapData = await loadMapData(areaSlug);

        // Check if we have valid map data
        if (!mapData || !mapData.features || mapData.features.length === 0) {
            console.error('No valid map features found. Map data:', mapData);
            throw new Error('No valid map features found. Please check if the gzipped GeoJSON files are accessible.');
        }

        if (geoJsonLayer) {
            mapInstance.removeLayer(geoJsonLayer);
        }

        geoJsonLayer = L.geoJSON(mapData, {
            style: getMapStyle,
            renderer: L.canvas({ padding: 1.0, tolerance: 8 }), // larger overscan to pre-render offscreen features
            interactive: false,
            pane: 'overlayPane',
            smoothFactor: 0.2 // lowered from 1 to 0.2
        }).addTo(mapInstance);

        // Fit bounds after adding the base map layer - only if we have valid bounds
        if (geoJsonLayer && geoJsonLayer.getBounds && geoJsonLayer.getBounds().isValid()) {
            mapInstance.fitBounds(geoJsonLayer.getBounds(), { padding: [50, 50] });
        } else {
            console.warn('GeoJSON layer has no valid bounds. Using default map center and zoom.');
            // Use default settings if no valid bounds
            mapInstance.setView(MAP_SETTINGS.DEFAULT_CENTER, MAP_SETTINGS.DEFAULT_ZOOM);
        }
    } catch (error) {
        console.error('Error loading map:', error);
        console.error('Error details:', error.stack);

        // Set default view on error
        mapInstance.setView(MAP_SETTINGS.DEFAULT_CENTER, MAP_SETTINGS.DEFAULT_ZOOM);

        alert('Error loading map data. Please check the console for details.');
    }
}

/**
 * Initializes area markers for unselected areas (UNIFIED mode only).
 * Should be called after an area is selected and areas data is available.
 * @param {Array} areas - Array of area objects from API
 * @param {string} selectedAreaSlug - Currently selected area slug
 * @param {Function} onAreaSelected - Callback when area marker is clicked (optional)
 */
export async function initializeAreaMarkersOnMap(areas, selectedAreaSlug, onAreaSelected = null) {
    if (LOGGING.ENABLED && LOGGING.GENERAL) {
        console.log('[Map] initializeAreaMarkersOnMap called');
        console.log('[Map] Areas:', areas);
        console.log('[Map] Selected area:', selectedAreaSlug);
    }

    if (!map || !areas) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log('[Map] Skipping area markers - map or areas not available');
        }
        return;
    }

    // Cache areas for later use
    cachedAreas = areas;
    currentSelectedAreaSlug = selectedAreaSlug;

    // Create area markers for unselected areas
    const markerGroup = createAreaMarkers(areas, selectedAreaSlug, map);

    if (!markerGroup) {
        // Feature is disabled or wrong mode
        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.log('[Map] Area markers not created - feature disabled or wrong mode');
        }
        return;
    }

    // Initialize click handlers for area markers
    await initializeAreaMarkerSelection(areas, map, onAreaSelected);

    if (LOGGING.ENABLED && LOGGING.GENERAL) {
        console.log('[Map] Area markers initialized and ready for interaction');
    }
}

/**
 * Clears area markers from the map.
 */
export function clearAreaMarkersOnMap() {
    removeAreaMarkers();
    cachedAreas = null;
    currentSelectedAreaSlug = null;

    if (LOGGING.ENABLED && LOGGING.DEBUG) {
        console.log('[Map] Area markers cleared');
    }
}

// Spot Management Functions
export function addSpot(spot, searchedTags = [], matchType = 'partial') {
    // Check if marker already exists (we'll need to track this differently with clustering)
    const existingMarker = findExistingMarker(spot.id);
    if (existingMarker) {
        return existingMarker;
    }

    if (LOGGING.ENABLED && LOGGING.VISIBILITY) {
        console.log(`Adding marker for spot ID: ${spot.id} at [${spot.coordinates.latitude}, ${spot.coordinates.longitude}]`);
    }

    const marker = createSpotMarker(spot, searchedTags, matchType);

    // Add to cluster group if available, otherwise add directly to map
    if (markerClusterGroup) {
        markerClusterGroup.addLayer(marker);
        // Store marker reference for later removal
        marker.spotId = spot.id;
    } else {
        marker.addTo(map);
    }

    spots.push(spot);
    logMarkerCreated(spot.id);
    return marker;
}

// Helper function to get accurate cluster count
function getClusterCount() {
    if (!markerClusterGroup) return 0;

    let clusterCount = 0;
    markerClusterGroup.eachLayer(layer => {
        // Check if this is a cluster (has multiple markers)
        if (layer.getChildCount && layer.getChildCount() > 1) {
            clusterCount++;
        }
    });
    return clusterCount;
}

// Helper function to find existing marker (works with both clustering and individual markers)
function findExistingMarker(spotId) {
    if (markerClusterGroup) {
        // Search through cluster group layers
        let foundMarker = null;
        markerClusterGroup.eachLayer(layer => {
            if (layer.spotId === spotId) {
                foundMarker = layer;
            }
        });
        return foundMarker;
    }
    return null; // For now, we'll assume no duplicates with clustering
}

export function removeSpot(spotId) {
    let marker = null;

    if (markerClusterGroup) {
        // Find and remove from cluster group
        markerClusterGroup.eachLayer(layer => {
            if (layer.spotId === spotId) {
                marker = layer;
                markerClusterGroup.removeLayer(layer);
            }
        });
    }

    if (marker) {
        spots = spots.filter(spot => spot.id !== spotId);
        logMarkerRemoved(spotId);
    }
}

function addMarkersInBatches(spotsForMap, searchedTags, batchSize = 20, matchTypeFn) {
    // If clustering is available, we can add more markers per batch
    const effectiveBatchSize = markerClusterGroup ? Math.max(batchSize, 50) : batchSize;

    let i = 0;
    const markersToAdd = [];

    function addBatch() {
        const batchStart = i;
        const batchEnd = Math.min(i + effectiveBatchSize, spotsForMap.length);

        // Create all markers for this batch
        for (let j = batchStart; j < batchEnd; j++) {
            const spot = spotsForMap[j];
            const matchType = matchTypeFn(spot);
            const marker = createSpotMarker(spot, searchedTags, matchType);
            if (marker) {
                marker.spotId = spot.id; // Store spot ID for later reference
                markersToAdd.push(marker);
                spots.push(spot);
            }
        }

        // Add markers based on available method
        if (markerClusterGroup && markersToAdd.length > 0) {
            // Use clustering for better performance
            const startTime = performance.now();
            markerClusterGroup.addLayers(markersToAdd);
            const endTime = performance.now();

            if (LOGGING.ENABLED && LOGGING.DEBUG) {
                console.log(`✅ Added ${markersToAdd.length} markers to cluster group in ${(endTime - startTime).toFixed(2)}ms`);
            }

            // Log clustering as a single batch operation instead of individual markers
            logMarkerCreated(`batch_${markersToAdd.length}_markers`);

            // Track clustering performance
            import('../utils/performance-monitor.js').then(({ logClusteringPerformance }) => {
                // Get accurate cluster count
                const clusterCount = getClusterCount();
                logClusteringPerformance(markersToAdd.length, clusterCount, endTime - startTime);
            });
        } else if (markersToAdd.length > 0) {
            // Fallback to individual markers
            markersToAdd.forEach(marker => {
                marker.addTo(map);
                if (LOGGING.ENABLED && LOGGING.DEBUG) {
                    console.log(`✅ Added individual marker for spot ${marker.spotId}`);
                }
            });
            // Log individual markers when not using clustering
            markersToAdd.forEach(marker => {
                logMarkerCreated(marker.spotId);
            });
        }

        i = batchEnd;

        if (i < spotsForMap.length) {
            // Use requestAnimationFrame for smoother UI
            window.requestAnimationFrame(addBatch);
        }
    }

    addBatch();
}

export function displaySpots(spotsToDisplay, searchedTags = [], shouldPan = true) {
    if (!Array.isArray(spotsToDisplay)) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) {
            console.error('displaySpots called with non-array:', spotsToDisplay);
        }
        return;
    }
    if (LOGGING.ENABLED && LOGGING.VISIBILITY) {
        console.log(`Attempting to display ${spotsToDisplay ? spotsToDisplay.length : 0} spots.`);
    }

    if (!spotsToDisplay || spotsToDisplay.length === 0) {
        if (LOGGING.ENABLED && LOGGING.VISIBILITY) {
            console.log('No spots to display.');
        }
        return;
    }

    // Clear existing markers first for better performance
    clearAllSpots();

    // Log spots loaded for performance monitoring
    logSpotsLoaded(spotsToDisplay.length);
    takeMemorySnapshot('before_display_spots');

    const lang = getCurrentLanguage ? getCurrentLanguage() : 'es';
    const spotsForMap = spotsToDisplay.map(spot => {
        const mapped = {
            ...spot,
            coordinates: {
                latitude: parseFloat(spot.latitude),
                longitude: parseFloat(spot.longitude)
            },
            tags: spot.tags || []
        };
        if (window.LOGGING && window.LOGGING.ENABLED && window.LOGGING.DEBUG) {
            console.log('[DEBUG][displaySpots] spot.id:', spot.id, 'cid_googleMaps:', spot.cid_googleMaps, 'mapped:', mapped.cid_googleMaps);
        }
        return mapped;
    });

    // Optimized batch marker addition
    addMarkersInBatches(
        spotsForMap,
        searchedTags,
        50, // Increased batch size for better performance
        (spot) => {
            const spotTags = (typeof spot.tags === 'string' ? spot.tags.split(',') : (spot.tags || []))
                .map(t => (t.slug || t).toLowerCase());
            const lowerSearched = (searchedTags || [])
                .map(t => typeof t === 'string' ? t.toLowerCase() : '')
                .filter(Boolean);
            const isExact = lowerSearched.length > 0 && lowerSearched.every(tag => spotTags.includes(tag));
            return isExact ? 'exact' : 'partial';
        }
    );

    // Improved: Open popup after zoom animation for single marker, with a short delay for smoothness
    // Also allow a forced open when the URL explicitly provided a single spot ID.
    const forceSinglePopupFromUrl = typeof window !== 'undefined' && window.__openSingleSpotFromUrl;
    const shouldAutoOpen = ((AUTO_OPEN_SINGLE_POPUP && spotsForMap.length === 1 && shouldPan) ||
        (forceSinglePopupFromUrl && spotsForMap.length === 1));

    if (shouldAutoOpen) {
        // Wait for markers to be added to cluster group
        setTimeout(() => {
            const markers = getMarkers();
            if (markers.length === 1) {
                const onlyMarker = markers[0];
                if (onlyMarker && map) {
                    if (isCardsMode) {
                        import('../cards/cards-manager.js').then(({ focusSpotCards }) => {
                            focusSpotCards({ spot: onlyMarker.options?.spot, spotId: onlyMarker.spotId });
                        });
                        return;
                    }
                    if (onlyMarker.openPopup) {
                        if (forceSinglePopupFromUrl && typeof window !== 'undefined') {
                            // Reset flag so it only runs once per URL load
                            window.__openSingleSpotFromUrl = false;
                        }
                        // If there's no pan/zoom movement, open immediately after a short delay.
                        if (!shouldPan) {
                            setTimeout(() => {
                                onlyMarker.openPopup();
                            }, 300);
                            return;
                        }

                        let pendingSinglePopup = true;
                        const onMoveEnd = () => {
                            if (pendingSinglePopup) {
                                pendingSinglePopup = false;
                                setTimeout(() => {
                                    onlyMarker.openPopup();
                                }, 1000); // 1000ms delay for smoothness for the popup to open after the zoom
                                map.off('moveend', onMoveEnd);
                            }
                        };
                        map.on('moveend', onMoveEnd);
                    }
                }
            }
        }, 100);
    }

    if (LOGGING.ENABLED && LOGGING.VISIBILITY) {
        console.log(`Added ${spotsForMap.length} new markers. Panning to view them.`);
    }

    // Pan to the new markers only if shouldPan is true
    if (shouldPan) {
        // Wait for markers to be added before panning
        setTimeout(() => {
            const markers = getMarkers();
            if (markers.length > 0) {
                panToMarkers(map, markers);
            }
        }, 50);
    }

    // Preload spot data and cards for all displayed spots in the background
    // This batch-loads everything so they're ready instantly when user clicks a spot
    // ORDER: spots-ids first, then cards-for-spots (explore cards already started earlier)
    if (spotsToDisplay && spotsToDisplay.length > 0) {
        const spotIds = spotsToDisplay
            .map(spot => spot.id)
            .filter(id => id != null);

        if (spotIds.length > 0) {
            // Preload spot data first (needed for auto_card checks)
            // This will trigger spots-ids.php
            import('../utils/cache/spots-cache.js').then(({ preloadSpots }) => {
                preloadSpots(spotIds);
            }).catch(error => {
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.warn('[Map] Failed to preload spots:', error);
                }
            });

            // Preload cards AFTER spots (order: explore cards -> spots-ids -> cards-for-spots)
            import('../utils/cache/cards-cache.js').then(({ preloadCardsForSpots }) => {
                preloadCardsForSpots(spotIds);
            }).catch(error => {
                // Silently fail - cards will load on-demand if preload fails
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.warn('[Map] Failed to preload cards:', error);
                }
            });
        }
    }
}

export function getMap() {
    return map;
}

export function getMarkers() {
    if (markerClusterGroup) {
        const markers = [];
        markerClusterGroup.eachLayer(layer => {
            markers.push(layer);
        });
        return markers;
    }
    return []; // Return empty array if no clustering
}

export function getSpots() {
    return spots;
}

export function clearAllSpots() {
    if (markerClusterGroup) {
        // Use clustering clear method
        markerClusterGroup.clearLayers();
        if (LOGGING.ENABLED && LOGGING.DEBUG) {
            console.log('✅ Cleared all markers from cluster group');
        }
    } else {
        // Fallback: remove individual markers
        const markers = getMarkers();
        markers.forEach(marker => {
            if (marker.remove) {
                marker.remove();
            }
        });
        if (LOGGING.ENABLED && LOGGING.DEBUG) {
            console.log(`✅ Removed ${markers.length} individual markers`);
        }
    }
    spots.length = 0; // Clear the spots array
    clearMarkerRegistry(); // Clear the marker registry for efficient lookups
    if (LOGGING.ENABLED && LOGGING.VISIBILITY) {
        console.log('Cleared all spots and markers');
    }
}

export function clearSpotsArray() {
    spots.length = 0; // Clear the array without reassigning the reference
}

export function setLocationMarker(latLng) {
    if (locationMarker) {
        locationMarker.setLatLng(latLng);
    } else {
        locationMarker = createAnimatedLocationMarker(latLng).addTo(map);
    }
    return locationMarker;
}

export function getLocationMarker() {
    return locationMarker;
}

export function invalidateMapSize() {
    if (map) {
        map.invalidateSize();
    }
}

// Listen for searchResults event and pass searchedTags
if (typeof window !== 'undefined') {
    document.addEventListener('searchResults', (e) => {
        if (e.detail && Array.isArray(e.detail.results)) {
            // Use the new centralized map view updater
            updateMapView({
                spotsToDisplay: e.detail.results,
                searchedTags: e.detail.searchedTags || []
            });
        } else if (Array.isArray(e.detail)) {
            // Backward compatibility: if only array is passed
            updateMapView({ spotsToDisplay: e.detail });
        } else {
            // Invalid event detail, do nothing or log error
            if (LOGGING.ENABLED && LOGGING.GENERAL) {
                console.error('searchResults event: detail is not an array or does not contain results array:', e.detail);
            }
        }
    });
} 